/**
 * Hint card content for the Template Wizard.
 *
 * Keys correspond to `data-hint-field` attribute values on field wrappers.
 * Each entry has a `title` and `body` (supports markdown via MarkdownContent).
 *
 * For TemplateStructureStep, keys are SOAP section names:
 *   subjective, objective, assessment, plan, freeform
 * For TemplatePreferencesStep, the key is "userPreferences".
 */
export const WIZARD_HINT_CONTENT = {
  subjective: {
    title: "Subjective Section Tips",
    body: `- **Show, don't tell when it comes to formatting** - Want bullet points? Add a dash or use the list option buttons directly rather than describing it in preferences. You can also do the same with bolds, new lines, and other formatting options.
- **If you want sub-sections, use clear titles** - Label every section explicitly (e.g., History:, Diet:).
- **Use parentheses for AI instructions or placeholders** - Brackets tell our AI what content to fill in. For example, 
  - Meds: (Name, Dose, Frequency). 
  - (Patient Name) is here on (Date) for a recheck appointment.`,
  },
  objective: {
    title: "Objective Section Tips",
    body: `- **Show, don't tell when it comes to formatting** - Want bullet points? Add a dash or use the list option buttons directly rather than describing it in preferences. You can also do the same with bolds, new lines, and other formatting options.
- **If you want sub-sections, use clear titles** - Label every section explicitly (e.g., Vitals:, Respiratory:).
- **Use parentheses for AI instructions or placeholders** - Brackets tell our AI what content to fill in. For example:
  - Masses: (Include size, description and location of each mass, if mentioned)
  - BCS: (1-9)
- **Include your default normals** - Place your normals directly into the template next to the corresponding body system, i.e. Capillary Refill Time: <2 seconds.`,
  },
  assessment: {
    title: "Assessment Section Tips",
    body: `- **Show, don't tell when it comes to formatting** - Want bullet points? Add a dash or use the list option buttons directly rather than describing it in preferences. You can also do the same with bolds, new lines, and other formatting options.
- **If you want sub-sections, use clear titles** - Label every section explicitly (e.g., Diagnosis:, Differentials:, Prognosis:).
- **Use parentheses for AI instructions or placeholders** - Brackets tell our AI what content to fill in. For example, 
  - DDX: (If differentials were discussed, list them here). 
  - Overall Patient Status: (Stable / Guarded / Critical)`,
  },
  plan: {
    title: "Plan Section Tips",
    body: `- **Show, don't tell when it comes to formatting** - Want bullet points? Add a dash or use the list option buttons directly rather than describing it in preferences. You can also do the same with bolds, new lines, and other formatting options.
- **If you want sub-sections, use clear titles** - Label every section explicitly (e.g., Medications:, Diagnostics:, Follow-up:, Client Education:).
- **Use parentheses for AI instructions or placeholders** - Brackets tell our AI what content to fill in. For example, 
  - Plan: (Include if owner declined treatment plans). 
  - Recheck: (3 months from today's date).`,
  },
  freeform: {
    title: "Template Content Tips",
    body: `- **Show, don't tell when it comes to formatting** - Want bullet points? Add a dash or use the list option buttons directly rather than describing it in preferences. You can also do the same with bolds, new lines, and other formatting options.
- **If you want sub-sections, use clear titles** - Label every section explicitly (e.g., Medications:, Diagnostics:, Follow-up:, Client Education:).
- **Use parentheses for AI instructions or placeholders** - Brackets tell our AI what content to fill in. For example, 
  - Medications: (Include Name, Dose, Route, Frequency mentioned). 
  - (Patient name) is here on (Date) for (Presenting complaint).
- **Include your default normals** - Place your normals directly into the template next to the corresponding body system, e.g., Capillary Refill Time: <2 seconds.
- **Use "if mentioned" for optional details** - e.g., (Site of administration, if mentioned) prevents the AI from guessing.
- **Match your PIMS** - Keep the same wording, order, and spacing you use in your actual records.`,
  },
  userPreferences: {
    title: "Writing Preferences Tips",
    body: `- This is where you can give our AI instructions that apply to the whole note rather than specific sections. For example, you might include instructions about tone (e.g., professional, concise, client-friendly), or verbosity (e.g., be brief, be detailed) or include instructions that may vary per appointment (e.g., bold all abnormal objective findings.).
- Be careful not to add contradictions (eg. be brief and be detailed) as our AI won't know which instruction to prioritize!
- You can start with some examples by clicking the buttons above the text box.`,
  },
};
